import { FormEvent, Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';
import { ErrorMessage } from 'ui/shared/messages';
import { NoneProvided } from 'ui/shared/noneProvided';

require('./EditableLabelField.css');

export type Props = {
    id: string;
    label?: string;
    labelKey?: string;
    value: string;
    maxLength: number;
    canEdit: boolean;
    editing?: boolean;
    saving?: boolean;
    errorMessage?: string | null;
    onChange(value: string): void;
};

export const EditableLabelField = ({ id, label, labelKey, value: initialValue, canEdit, maxLength, editing = false, saving = false, errorMessage, onChange }: Props) => {
    const { t } = useTranslation();
    
    const [ value, setValue ] = useState(initialValue);
    const [ isEditing, setIsEditing ] = useState(editing);

    //
    // Reset our internal state when the input value changes
    //
    useEffect(() => {
        setValue(initialValue);
        setIsEditing(editing);
    }, [ initialValue, editing, setValue, setIsEditing ]);

    const handleChangeValue = useCallback((e: FormEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget;
        setValue(value);
    }, [ setValue ]);

    const handleClickEdit = useCallback(() => {
        setIsEditing(true);
    }, [ setIsEditing ]);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        onChange && onChange(value);
        e.preventDefault();
    };
    
    const handleClickCancel = useCallback(() => {
        setValue(initialValue);
        setIsEditing(false);
    }, [ initialValue, setValue, setIsEditing ]);
    

    if (isEditing && canEdit) {
        return (
            <form
                id={ `${id}-form` }
                className="ui form editable-label-field"
                onSubmit={ handleSubmit }
            >
                { (label || labelKey) &&
                    <label htmlFor={ id }>
                        { label || t(labelKey!) }
                    </label>
                }
                <div className="_editor">
                    <input
                        type="text"
                        id={ id }
                        maxLength={ maxLength }
                        autoFocus
                        value={ value }
                        onChange={ handleChangeValue }
                        disabled={ saving }
                    />
                    <span className="_buttons">
                        { !saving &&
                            <Fragment>
                                <button
                                    type="submit"
                                    className="save-button ui tiny icon button"
                                    disabled={ saving }
                                >
                                    <i className="ui grey check icon"/>
                                </button>
                                <button
                                    type="button"
                                    className="cancel-button ui tiny icon button"
                                    onClick={ handleClickCancel }
                                    disabled={ saving }
                                >
                                    <i className="ui grey cancel icon"/>
                                </button>
                            </Fragment>
                        }
                        { saving &&
                            <LoadingSpinner size="tiny" textKey="SAVING"/>
                        }
                    </span>
                </div>
                <ErrorMessage message={ errorMessage }/>
            </form>
        );
    }
    else {
        return (
            <div className="editable-label-field">
                { (label || labelKey) &&
                    <label htmlFor={ id }>
                        { label || t(labelKey!) }
                    </label>
                }
                <div className="_value">
                    { initialValue || <NoneProvided/> }
                </div>
                { canEdit &&
                    <button
                        type="button"
                        className="_edit-button ui tiny icon button"
                        onClick={ handleClickEdit }
                    >
                        <i className="ui grey pencil icon"/>
                    </button>
                }
            </div>
        );
    }
};
